import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { FilterOption } from "src/config/filterConfig";

interface MultiSelectFilterProps {
  /** Available options to select from */
  options: FilterOption[];

  /** Currently selected values */
  selectedValues: string[];

  /** Callback when selection changes */
  onChange: (values: string[]) => void;

  /** Maximum number of options to show before "Show more" */
  maxVisible?: number;

  /** Whether to show a search box for filtering options */
  searchable?: boolean;

  /** Whether to show counts next to options */
  showCounts?: boolean;

  /** Loading state */
  isLoading?: boolean;

  /** Placeholder for search input */
  searchPlaceholder?: string;

  /** Custom render function for option label */
  renderOptionLabel?: (option: FilterOption) => React.ReactNode;

  /** Maximum height for the expanded options list (enables scrolling when exceeded) */
  maxExpandedHeight?: number;
}

/**
 * Amazon-style multi-select filter with checkboxes
 *
 * Features:
 * - Checkbox selection with counts
 * - "Show more/less" for long lists
 * - Optional search box for filtering
 * - Highlighted selected items
 * - Loading state
 */
const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  options,
  selectedValues,
  onChange,
  maxVisible = 5,
  searchable = false,
  showCounts = true,
  isLoading = false,
  searchPlaceholder = "Search...",
  renderOptionLabel,
  maxExpandedHeight = 300,
}) => {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query),
    );
  }, [options, searchQuery]);

  // Sort options: selected first, then by count (if available), then alphabetically
  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      // Selected items first
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then by count (higher first)
      if (a.count !== undefined && b.count !== undefined) {
        if (a.count !== b.count) return b.count - a.count;
      }

      // Then alphabetically
      return a.label.localeCompare(b.label);
    });
  }, [filteredOptions, selectedValues]);

  // Determine visible options
  const visibleOptions = useMemo(() => {
    if (showAll || searchQuery) return sortedOptions;
    return sortedOptions.slice(0, maxVisible);
  }, [sortedOptions, showAll, maxVisible, searchQuery]);

  const hiddenCount = sortedOptions.length - maxVisible;
  const showToggle = !searchQuery && sortedOptions.length > maxVisible;

  const handleToggle = (value: string) => {
    const isSelected = selectedValues.includes(value);
    if (isSelected) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 1 }}>
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              height: 28,
              backgroundColor: alpha(theme.palette.action.hover, 0.5),
              borderRadius: 1,
              mb: 0.5,
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          />
        ))}
      </Box>
    );
  }

  if (options.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 1, fontStyle: "italic" }}
      >
        No options available
      </Typography>
    );
  }

  return (
    <Box>
      {/* Search box */}
      {searchable && options.length > maxVisible && (
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <Search
                sx={{
                  fontSize: "1rem",
                  color: theme.palette.text.disabled,
                  mr: 0.5,
                }}
              />
            ),
          }}
          sx={{
            mb: 1,
            "& .MuiOutlinedInput-root": {
              fontSize: "0.8125rem",
              backgroundColor: alpha(theme.palette.background.default, 0.5),
            },
            "& .MuiOutlinedInput-input": {
              py: 0.75,
            },
          }}
          fullWidth
        />
      )}

      {/* Options list */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          // Enable scrolling when expanded with many options
          ...(showAll &&
            visibleOptions.length > maxVisible && {
              maxHeight: maxExpandedHeight,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5, // Padding for scrollbar
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.3),
                },
              },
            }),
        }}
      >
        {visibleOptions.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggle(option.value)}
                  size="small"
                  sx={{
                    p: 0.5,
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.125rem",
                    },
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.8125rem",
                      color: isSelected
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                      fontWeight: isSelected ? 500 : 400,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderOptionLabel
                      ? renderOptionLabel(option)
                      : option.label}
                  </Typography>
                  {showCounts && option.count !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.disabled,
                        fontSize: "0.75rem",
                        flexShrink: 0,
                      }}
                    >
                      ({option.count.toLocaleString()})
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                mx: 0,
                py: 0.25,
                px: 0.5,
                borderRadius: 1,
                width: "100%",
                "& .MuiFormControlLabel-label": {
                  flex: 1,
                  minWidth: 0,
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.action.hover, 0.5),
                },
                ...(isSelected && {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                }),
              }}
            />
          );
        })}
      </Box>

      {/* No results message */}
      {searchQuery && visibleOptions.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 1, fontStyle: "italic", fontSize: "0.8125rem" }}
        >
          No matches for "{searchQuery}"
        </Typography>
      )}

      {/* Show more/less toggle */}
      {showToggle && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setShowAll(!showAll)}
          sx={{
            mt: 1,
            fontSize: "0.8125rem",
            color: theme.palette.primary.main,
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {showAll ? "Show less" : `Show ${hiddenCount} more`}
        </Link>
      )}
    </Box>
  );
};

export default MultiSelectFilter;
